/**
 * Custom validator registry for EntityForm.
 *
 * Provides a centralized registry where modules can register field-level
 * refinements and cross-field validators by key. These keys are then
 * referenced in FieldValidationConfig.custom and CrossFieldRule.validator.
 */

import type { CrossFieldRule } from '../types.ts';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** A cross-field validator function. */
export type CrossFieldValidator = (
  values: Record<string, unknown>,
  rule: CrossFieldRule,
) => { field: string; message: string } | null;

/** A stored refinement entry. */
export interface RefinementEntry {
  fn: (value: unknown) => boolean;
  message: string;
}

// ---------------------------------------------------------------------------
// Registry implementation
// ---------------------------------------------------------------------------

const refinements = new Map<string, RefinementEntry>();
const crossFieldValidators = new Map<string, CrossFieldValidator>();

/**
 * Singleton validation registry.
 *
 * Modules register custom validation functions here so they can be
 * referenced by string key inside configuration objects.
 */
export const validationRegistry = {
  /**
   * Register a field-level Zod refinement.
   *
   * @param key   - Unique key referenced by FieldValidationConfig.custom.
   * @param fn    - Predicate that returns `true` when the value is valid.
   * @param message - Error message shown when the refinement fails.
   */
  registerRefinement(
    key: string,
    fn: (value: unknown) => boolean,
    message: string,
  ): void {
    refinements.set(key, { fn, message });
  },

  /**
   * Register a cross-field validator.
   *
   * @param key - Unique key referenced by CrossFieldRule.validator.
   * @param fn  - Validator function that receives all form values and the rule.
   */
  registerCrossFieldValidator(key: string, fn: CrossFieldValidator): void {
    crossFieldValidators.set(key, fn);
  },

  /**
   * Get a registered refinement by key.
   *
   * @returns The refinement entry, or `undefined` if not found.
   */
  getRefinement(key: string): RefinementEntry | undefined {
    return refinements.get(key);
  },

  /**
   * Get a registered cross-field validator by key.
   *
   * @returns The cross-field validator function, or `undefined` if not found.
   */
  getCrossFieldValidator(key: string): CrossFieldValidator | undefined {
    return crossFieldValidators.get(key);
  },
} as const;
